import type { BillingDecisionTrace } from './ticket-cost-types';

/** IDs are only used to join evidence. Missing names must never expose an ID. */
export const billingEntityName = (
  trace: BillingDecisionTrace,
  id: string | null,
  fallback = 'Name unavailable'
) => {
  if (!id) return fallback;
  const name = trace.entityNames?.[id];
  if (name) return name;
  const stage = trace.intervals.find(
    (interval) => interval.stageId === id && interval.stageName
  )?.stageName;
  if (stage) return stage;
  const output = trace.steps.find(
    (step) => step.code === 'BILLING_LOCATION' && step.entityId === id
  );
  if (output?.explanation.startsWith('Bill to '))
    return output.explanation.slice(8).replace(/\.$/, '');
  return fallback;
};

export const billingOutputLabel = (
  trace: BillingDecisionTrace,
  output: BillingDecisionTrace['steps'][number]
) =>
  output.entityId && trace.entityNames?.[output.entityId]
    ? `Bill to ${trace.entityNames[output.entityId]}.`
    : output.explanation;

const inputLabels: Record<string, string> = {
  containerId: 'Container',
  siteId: 'Site',
  wellId: 'Well',
  regionId: 'Region',
  operatorId: 'Operator',
  operatorCompanyId: 'Operator',
  serviceTypeId: 'Service',
  freightTypeId: 'Freight',
  waterTypeId: 'Freight',
  rateModelId: 'Rate model',
};
const words = (value: string) =>
  value
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/^./, (character) => character.toUpperCase());
export const billingDecisionInputs = (trace: BillingDecisionTrace) => {
  const inputs: Array<{ label: string; value: string }> = [];
  for (const [key, label] of Object.entries(inputLabels)) {
    const id = trace.inputs[key];
    if (!id) continue;
    const nameKey = key.replace(/Id$/, 'Name');
    inputs.push({
      label,
      value: trace.inputs[nameKey] || billingEntityName(trace, id),
    });
  }
  if (trace.inputs.billingSide)
    inputs.push({
      label: 'Billing side',
      value: words(trace.inputs.billingSide),
    });
  if (trace.inputs.category)
    inputs.push({
      label: 'Ticket category',
      value: words(trace.inputs.category),
    });
  return inputs;
};

const titles: Record<string, string> = {
  RULE_CHECK: 'Location rule check',
  TICKET_CODES: 'Ticket coding',
  CODED_LOCATION: 'Coded location',
  PRODUCT_SITE_AFE: 'Product billing rule',
  CONTAINER_AFE: 'Container expenditure code',
  CLUSTER_AFE: 'Cluster expenditure code',
  EXPLICIT_WELL: 'Ticket well',
  CONTAINER_SITE: 'Load point',
  CONTAINER_WELLS: 'Associated wells',
  SITE_FALLBACK: 'Site fallback',
  PARENT_SITE_AFE: 'Parent site',
  MATCHED_STAGE: 'Stage at the ticket time',
  DEFAULT_STAGE: 'Default stage',
  LOCATION_DEFAULT_STAGE: 'Location default stage',
  STAGE_SHUT_IN: 'Shut-in check',
  STAGE_FREIGHT: 'Freight compatibility',
  STAGE_SERVICE: 'Service compatibility',
  STAGE_INTERVAL: 'Schedule timing',
  STAGE_MISSING: 'Stage availability',
  WELL_EXCLUDED: 'Well eligibility',
  STAGE_AFE_PERMISSION: 'Stage expenditure code permission',
  CODING_AFE_PRECEDENCE: 'Coded expenditure code',
  STAGE_AFE: 'Stage expenditure code',
  FINAL_BILLING_LOCATION: 'Final billing location',
  BILLING_LOCATION: 'Billing result',
};
type BillingDecisionStep = BillingDecisionTrace['steps'][number];

const hiddenChecks = new Set([
  'SCHEDULE_SCOPE',
  'STAGE_ELIGIBLE',
  'STAGE_SELECTED',
]);
const specificStageChecks = new Set([
  'STAGE_SHUT_IN',
  'STAGE_FREIGHT',
  'STAGE_SERVICE',
  'STAGE_INTERVAL',
  'STAGE_MISSING',
]);

function checkDescription(
  trace: BillingDecisionTrace,
  step: BillingDecisionStep,
  selectedStages: Set<BillingDecisionStep['entityId']>
): string {
  switch (step.code) {
    case 'BILLING_LOCATION':
      return billingOutputLabel(trace, step);
    case 'STAGE_AFE_PERMISSION':
      return step.outcome === 'rejected'
        ? 'The location remains eligible, but its stage expenditure code cannot replace the location expenditure code.'
        : 'The service and freight rules allow the stage expenditure code to be used.';
    case 'WELL_EXCLUDED':
      return 'No eligible stage was found for this well at the ticket time.';
    case 'DEFAULT_STAGE':
      return step.outcome === 'unavailable'
        ? 'No default stage is available.'
        : 'No specific stage matched, so the default stage was used.';
    case 'MATCHED_STAGE':
      return selectedStages.has(step.entityId)
        ? `${step.explanation} The schedule’s priority and timing rules chose this stage.`
        : step.explanation;
    case 'STAGE_AFE':
      return 'The selected stage’s expenditure code is used where provided.';
    default:
      return step.explanation;
  }
}

export const billingDecisionChecks = (trace: BillingDecisionTrace) => {
  const explainedEntities = new Set(
    trace.steps
      .filter((step) => specificStageChecks.has(step.code))
      .map((step) => step.entityId)
  );
  const selectedStages = new Set(
    trace.steps
      .filter((step) => step.code === 'STAGE_SELECTED')
      .map((step) => step.entityId)
  );

  return trace.steps
    .filter((step) => !hiddenChecks.has(step.code))
    .filter(
      (step) =>
        step.code !== 'WELL_EXCLUDED' || !explainedEntities.has(step.entityId)
    )
    .map((step) => ({
      ...step,
      title: titles[step.code] ?? 'Billing check',
      entityName: step.entityId
        ? billingEntityName(trace, step.entityId)
        : undefined,
      description: checkDescription(trace, step, selectedStages),
    }));
};
